// src/components/User/components/UserDashboardComponents/Repair/DashboardRepairList.tsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { RootState, AppDispatch } from "../../../../../store/store";
import type { Repair } from "../../../../../store/Slice/repairSlice";
import { fetchRepairsAsync, deleteRepairAsync, clearMessages, approveRepairAsync } from "../../../../../store/Slice/repairSlice";
import debounce from "lodash/debounce";
import { FadeLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import RepairDetails from "./RepairDetails";
import { IoEyeOutline } from "react-icons/io5";
import { BsDownload } from "react-icons/bs";
import { RiDeleteBinLine } from "react-icons/ri";
import { MdOutlinePendingActions } from "react-icons/md";
import { FiEdit } from "react-icons/fi";
import EditRepairModal from "./EditRepairModel";
import { handleRepairDownload } from "./repairDownload"; // adjust path if needed


interface Props {
  refreshTrigger?: boolean;
  cardFilter?: "all" | "repair" | "replacement" | "pending" | "delayed";
}

const DashboardRepairList: React.FC<Props> = ({ refreshTrigger, cardFilter }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { items = [], total = 0, status, error, message } = useSelector((s: RootState) => s.repairs);
  const { user } = useSelector((state: RootState) => state.auth);
  const loading = status === "loading";

  // Filters & States
  const [openAction, setOpenAction] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [dateErrors, setDateErrors] = useState({ fromDate: "", toDate: "" });
  const [requestType, setRequestType] = useState("all");
  const [statusFilter] = useState(cardFilter || "all");
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const [showDetails, setShowDetails] = useState(false);
  const [selectedRepair, setSelectedRepair] = useState<Repair | null>(null);
  const [showAlert, setShowAlert] = useState(false);
  const [repairToDelete, setRepairToDelete] = useState<string | null>(null);

  const [showEdit, setShowEdit] = useState(false);
  const [editRepair, setEditRepair] = useState<Repair | null>(null);


  const today = new Date().toISOString().split("T")[0];

  // Validate Dates
  const validateDates = (field: "fromDate" | "toDate", value: string) => {
    const dateValue = new Date(value);
    const todayDate = new Date(today);
    if (dateValue > todayDate) {
      setDateErrors(prev => ({ ...prev, [field]: "Date cannot be in the future." }));
    } else {
      setDateErrors(prev => ({ ...prev, [field]: "" }));
    }
    if (fromDate && toDate) {
      if (new Date(fromDate) > new Date(toDate)) {
        setDateErrors({
          fromDate: "From Date cannot be after To Date.",
          toDate: "To Date cannot be before From Date."
        });
      } else {
        setDateErrors({ fromDate: "", toDate: "" });
      }
    }
  };

  // Debounced fetch using ref to satisfy hooks linting
  type FetchParams = Record<string, unknown>;
  const debouncedFetchRef = React.useRef<((params: FetchParams) => void) & { cancel?: () => void } | null>(null);
  React.useEffect(() => {
    debouncedFetchRef.current = debounce((params: FetchParams) => {
      dispatch(fetchRepairsAsync(params));
    }, 400);
    return () => {
      if (debouncedFetchRef.current?.cancel) debouncedFetchRef.current.cancel();
    };
  }, [dispatch]);

  // (removed unused getComputedStatus to avoid lint warnings)

  // Fetch repairs when filters/page change
  useEffect(() => {
    const params: FetchParams = { page: currentPage, limit: rowsPerPage };
    if (searchQuery.trim() !== "") params.search = searchQuery.trim();
    if (requestType !== "all") params.requestType = requestType;
    if (statusFilter !== "all") params.status = statusFilter;
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;
    if (debouncedFetchRef.current) debouncedFetchRef.current(params);
    return () => {
      if (debouncedFetchRef.current?.cancel) debouncedFetchRef.current.cancel();
    };
  }, [searchQuery, requestType, statusFilter, currentPage, fromDate, toDate, refreshTrigger]);

  // Toast messages
  useEffect(() => {
    if (message) toast.success(String(message));
    if (error) toast.error(String(error));
    if (message || error) dispatch(clearMessages());
  }, [message, error, dispatch]);

  // Refresh list when repair approved
  useEffect(() => {
    if (message === "Approved") {
      dispatch(fetchRepairsAsync({ page: currentPage, limit: rowsPerPage }));
    }
  }, [message, dispatch, currentPage, rowsPerPage]);

  // Handlers
  const handleView = (item: Repair) => {
    setSelectedRepair(item);
    setShowDetails(true);
  };

  const confirmDelete = async () => {
    if (repairToDelete) {
      await dispatch(deleteRepairAsync(repairToDelete));
      toast.error("Repair deleted", { toastId: "repair-deleted" });
      setShowAlert(false);
      setRepairToDelete(null);
      dispatch(fetchRepairsAsync({ page: currentPage, limit: rowsPerPage }));
    }
  };

  const handleEdit = (r: Repair) => {
    setEditRepair(r);
    setShowEdit(true);
  };


  const handleDownload = async (r: Repair) => {
    try {
      await handleRepairDownload(r);
    } catch (err) {
      console.error(err);
      toast.error("Failed to download PDF");
    }
  };


  // Pagination helper
  const totalPages = Math.ceil(total / rowsPerPage);

  // Loading / error UI
  if (loading) {
    return (
      <div className="p-5 text-center text-gray-600 dark:text-gray-300">
        <FadeLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-5 text-center text-red-600">
        Error loading repairs: {String(error)}
        <p className="text-sm text-gray-500 mt-2">
          Please try logging in again or check your network connection.
        </p>
      </div>
    );
  }

  return (
    <div className="border-2 border-gray-300 rounded-lg p-5 mt-10 overflow-visible lg:text-lg md:text-sm text-xs mb-20">
      {/* Delete Confirmation Modal */}
      {showAlert && (
        <div className="fixed inset-0 flex items-center justify-center p-4 backdrop-filter backdrop-blur-md z-10">
          <div className="bg-red-100 p-6 rounded-lg shadow-xl max-w-sm w-full">
            <h3 className="text-xl font-bold text-red-600 mb-4">Confirm Deletion</h3>
            <p className="mb-4 text-gray-700">Are you sure you want to delete this repair request?</p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setShowAlert(false); setRepairToDelete(null); }}
                className="bg-white text-gray-800 font-bold py-2 px-4 rounded hover:bg-gray-100 transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="bg-red-600 text-white font-bold py-2 px-4 rounded hover:bg-red-700 transition-colors cursor-pointer"
              >
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header and Filters */}
      <div className="flex justify-between lg:flex-row xl:flex-row flex-col items-start mb-4">
        <div className="flex items-center gap-3">
          <p className="font-semibold lg:text-lg xl:text-xl text-sm text-gray-800 dark:text-white">
            Recent Repair Requests
          </p>
        </div>

        <div className="flex items-end gap-4 xl:flex-row lg:flex-row flex-col xl:text-sm lg:text-sm text-xs mt-2">
          {/* Search Bar */}
          <span className="search border-2 bg-white dark:bg-zinc-800 w-full flex items-center justify-center rounded-lg">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="p-2 size-10"
            >
              <path
                fillRule="evenodd"
                d="M10.5 3.75a6.75 6.75 0 1 0 0 13.5 6.75 6.75 0 0 0 0-13.5ZM2.25 10.5a8.25 8.25 0 1 1 14.59 5.28l4.69 4.69a.75.75 0 1 1-1.06 1.06l-4.69-4.69A8.25 8.25 0 0 1 2.25 10.5Z"
                clipRule="evenodd"
              />
            </svg>
            <input
              id="search"
              name="search"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              type="search"
              placeholder="Search Repairs..."
              className="w-full p-2 font-semibold lg:text-sm text-xs"
            />
          </span>

          {/* Date Filters */}
          <div className="flex flex-row md:flex-row gap-4 w-full">
            {/* From Date */}
            <div className="flex flex-col w-full md:w-1/2">
              <label
                htmlFor="from-date"
                className="text-xs text-gray-700 dark:text-white font-medium mb-1"
              >
                From Date
              </label>
              <input
                type="date"
                id="from-date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  validateDates("fromDate", e.target.value);
                  setCurrentPage(1);
                }}
                className={`p-2 border bg-white dark:bg-zinc-800 ${dateErrors.fromDate
                  ? "border-red-500"
                  : "border-gray-300 dark:border-zinc-600"
                  } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                max={today}
              />
              {dateErrors.fromDate && (
                <span className="text-xs text-red-500 mt-1">
                  {dateErrors.fromDate}
                </span>
              )}
            </div>

            {/* To Date */}
            <div className="flex flex-col w-full md:w-1/2">
              <label
                htmlFor="to-date"
                className="text-xs text-gray-700 dark:text-white font-medium mb-1"
              >
                To Date
              </label>
              <input
                type="date"
                id="to-date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  validateDates("toDate", e.target.value);
                  setCurrentPage(1);
                }}
                className={`p-2 border bg-white dark:bg-zinc-800 ${dateErrors.toDate
                  ? "border-red-500"
                  : "border-gray-300 dark:border-zinc-600"
                  } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                max={today}
              />
              {dateErrors.toDate && (
                <span className="text-xs text-red-500 mt-1">
                  {dateErrors.toDate}
                </span>
              )}
            </div>
          </div>

          {/* Request Type Filter */}
          <select
            value={requestType}
            onChange={(e) => {
              setRequestType(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full p-2 border border-gray-300 rounded-md text-gray-700 bg-white dark:bg-zinc-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#0A2975]"
          >
            <option value="all">All Types</option>
            <option value="repair">Repair</option>
            <option value="replacement">Replacement</option>
          </select>
        </div>
      </div>

      <div className="w-full">
        {/* Desktop View: Standard Table */}
        <div className="hidden lg:block">
          <table className="w-full text-xs mb-4">
            <thead>
              <tr className="bg-gray-200 dark:bg-zinc-950 text-xs">
                <th className="p-2 text-xl text-start">Order Number</th>
                <th className="p-2 text-xl text-start">Generated By</th>
                <th className="p-2 text-xl text-start">Company</th>
                <th className="p-2 text-xl">Receiving Date</th>
                <th className="p-2 text-xl">Dispatch Date</th>
                <th className="p-2 text-xl">Type</th>
                <th className="p-2 text-xl">Server ID</th>
                <th className="p-2 text-xl">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length > 0 ? (
                items.map((r) => (
                  <tr
                    key={r._id}
                    className="border-b border-gray-200 dark:border-zinc-600 odd:bg-white dark:odd:bg-zinc-800 even:bg-gray-50 even:dark:bg-zinc-900"
                  >
                    <td className="text-start">
                      <span className="p-2 text-blue-800 dark:text-blue-400 font-bold hover:underline text-sm">
                        {r.orderNumber}
                      </span>
                    </td>
                    <td className="p-2 flex items-center gap-3">
                      <div className="flex flex-col">
                        <span className="text-start font-semibold text-sm uppercase">
                          {r.createdBy?.username || "N/A"}
                        </span>
                        <span className="text-start">
                          {r.createdBy?.employeeId || "N/A"}
                        </span>
                      </div>
                    </td>
                    <td className="lg:p-2 p-1 text-start">
                      <span className="font-semibold text-lg uppercase">
                        {r.companyName}
                      </span>
                    </td>
                    <td className="lg:p-2 p-1 font-semibold text-sm">
                      {r.receivingDate
                        ? new Date(r.receivingDate).toLocaleDateString()
                        : "-"}
                    </td>
                    <td className="lg:p-2 p-1 font-semibold text-sm">
                      {r.expectedDeliveryDate
                        ? new Date(r.expectedDeliveryDate).toLocaleDateString()
                        : "-"}
                    </td>
                    <td className="lg:p-2 p-1">
                      <span
                        className={`px-2 py-1 uppercase rounded-full font-bold ${r.requestType === "repair"
                          ? "bg-blue-200 text-blue-600"
                          : "bg-green-200 text-green-600"
                          }`}
                      >
                        {r.requestType?.toUpperCase() || "N/A"}
                      </span>
                    </td>
                    <td className="lg:p-2 p-1 text-center font-semibold text-sm">
                      {r.serverId || "-"}
                    </td>
                    <td className="px-4 py-2 relative">

                      <div className="relative group inline-block">

                        {/* MAIN BUTTON */}
                        <button className=" justify-center text-sm shadow-sm">
                          …
                        </button>

                        {/* TOP DROPDOWN */}
                        <div
                          className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2
                 bg-white border shadow-lg rounded-lg p-2 w-40 
                 opacity-0 invisible group-hover:opacity-100 group-hover:visible
                 transition-all duration-200 z-50"
                        >
                          {/* OPTION */}
                          <div
                            className="flex items-center gap-2 p-2 rounded hover:bg-gray-100 cursor-pointer"
                            onClick={() => handleView(r)}
                          >
                            <IoEyeOutline className="text-lg" />
                            <span className="text-sm">View</span>
                          </div>

                          <div
                            className="flex items-center gap-2 p-2 rounded hover:bg-gray-100 cursor-pointer"
                            onClick={() => handleDownload(r)}
                          >
                            <BsDownload className="text-lg" />
                            <span className="text-sm">Download</span>
                          </div>

                          {(user.userType === "admin" || user.userType === "subadmin") && (
                            <div
                              className="flex items-center gap-2 p-2 rounded hover:bg-gray-100 cursor-pointer text-red-500"
                              onClick={() => {
                                setRepairToDelete(r._id);
                                setShowAlert(true);
                              }}
                            >
                              <RiDeleteBinLine className="text-lg" />
                              <span className="text-sm">Delete</span>
                            </div>
                          )}

                          {(user.userType === "admin" || user.userType === "subadmin") && !r.isApproved && (
                            <div
                              className="flex items-center gap-2 p-2 rounded hover:bg-gray-100 cursor-pointer"
                              onClick={() => dispatch(approveRepairAsync(r._id))}
                            >
                              <MdOutlinePendingActions className="text-lg text-yellow-600" />
                              <span className="text-sm">Approve</span>
                            </div>
                          )}

                          {r.isApproved && (
                            <div className="flex items-center gap-2 p-2 rounded cursor-default text-green-600">
                              <span className="text-lg">✅</span>
                              <span className="text-sm">Approved</span>
                            </div>
                          )}

                          <div
                            className="flex items-center gap-2 p-2 rounded hover:bg-gray-100 cursor-pointer"
                            onClick={() => handleEdit(r)}
                          >
                            <FiEdit className="text-lg" />
                            <span className="text-sm">Edit</span>
                          </div>
                        </div>
                      </div>

                    </td>


                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="text-center p-4 text-gray-500">
                    No repairs found for the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile View: Stacked Cards */}
        <div className="lg:hidden space-y-4">
          {items.length > 0 ? (
            items.map((r) => (
              <div
                key={r._id}
                className="border border-gray-300 rounded-md text-xs"
              >
                <div className="flex border-b p-2">
                  <span className="w-1/3 font-semibold text-gray-600 dark:text-gray-300 text-start">
                    Order Number:
                  </span>
                  <span className="w-2/3 text-blue-800 dark:text-blue-500 font-bold text-left">
                    {r.orderNumber}
                  </span>
                </div>

                <div className="flex border-b p-2 items-center gap-2">
                  <span className="w-1/3 font-semibold text-gray-600 dark:text-gray-300 text-start">
                    Generated By:
                  </span>
                  <div className="w-2/3 flex gap-2 items-center text-left">
                    <div className="text-left">
                      <p>{r.createdBy?.username || "N/A"}</p>
                      <p className="text-gray-500 dark:text-gray-300">
                        {r.createdBy?.employeeId}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex border-b p-2">
                  <span className="w-1/3 font-semibold text-gray-600 dark:text-gray-300 text-start">
                    Company:
                  </span>
                  <span className="w-2/3 text-left">{r.companyName}</span>
                </div>

                <div className="flex border-b p-2">
                  <span className="w-1/3 font-semibold text-gray-600 dark:text-gray-300 text-start">
                    Receiving Date:
                  </span>
                  <span className="w-2/3 text-left">
                    {r.receivingDate
                      ? new Date(r.receivingDate).toLocaleDateString()
                      : "-"}
                  </span>
                </div>

                <div className="flex border-b p-2">
                  <span className="w-1/3 font-semibold text-gray-600 dark:text-gray-300 text-start">
                    Dispatch Date:
                  </span>
                  <span className="w-2/3 text-left">
                    {r.expectedDeliveryDate
                      ? new Date(r.expectedDeliveryDate).toLocaleDateString()
                      : "-"}
                  </span>
                </div>

                <div className="flex border-b p-2">
                  <span className="w-1/3 font-semibold text-gray-600 dark:text-gray-300 text-start">
                    Type:
                  </span>
                  <span className="w-2/3 text-left">
                    <span
                      className={`px-2 py-1 uppercase rounded-full font-bold ${r.requestType === "repair"
                        ? "bg-blue-200 text-blue-600"
                        : "bg-green-200 text-green-600"
                        }`}
                    >
                      {r.requestType?.toUpperCase() || "N/A"}
                    </span>
                  </span>
                </div>

                <div className="flex border-b p-2">
                  <span className="w-1/3 font-semibold text-gray-600 dark:text-gray-300 text-start">
                    Server ID:
                  </span>
                  <span className="w-2/3 text-left">{r.serverId || "-"}</span>
                </div>

                <div className="flex justify-end gap-4 p-2 text-xl">
                  <IoEyeOutline
                    onClick={() => handleView(r)}
                    className="hover:bg-blue-800 p-1 rounded-sm hover:text-white duration-200 cursor-pointer"
                  />
                  <BsDownload
                    onClick={() => handleDownload(r)}
                    className="hover:bg-blue-800 p-1 rounded-sm hover:text-white duration-200 cursor-pointer"
                  />
                  {(user.userType === "admin" || user.userType === "subadmin") && (
                    <RiDeleteBinLine
                      onClick={() => {
                        setRepairToDelete(r._id);
                        setShowAlert(true);
                      }}
                      className="text-red-500 hover:bg-blue-800 p-1 rounded-sm duration-200 cursor-pointer"
                    />
                  )}
                  {/* Approve Button (admin + subadmin only) */}
                  {(user.userType === "admin" || user.userType === "subadmin") && !r.isApproved && (
                    <button
                      onClick={() => dispatch(approveRepairAsync(r._id))}
                      className="
       py-1 sm:px-0 sm:py-0 
      rounded-md
       text-yellow-600 
      flex items-center justify-center
      text-xs sm:text-sm
      gap-1 sm:gap-2
      hover:bg-blue-800 active:scale-95 transition-all
    "
                    >
                      <MdOutlinePendingActions className="text-base sm:text-lg" />
                      <span className="hidden">Approve</span>
                    </button>
                  )}


                  {/* Approved Badge */}
                  {r.isApproved && (
                    <span
                      className="
       sm:px-0 sm:py-0 
      rounded-md
      text-green-600 
      flex items-center justify-center
      text-xs sm:text-sm
      gap-1 sm:gap-2
      hover:bg-green-700 active:scale-95 transition-all
    "
                    >
                      <span className="text-base sm:text-lg">✅</span>
                      <span className="hidden ">Approved</span>
                    </span>
                  )}


                  <FiEdit
                    onClick={() => handleEdit(r)}
                    className="hover:bg-blue-800 p-1 rounded-sm hover:text-white cursor-pointer"
                  />



                </div>
              </div>
            ))
          ) : (
            <div className="text-center p-4 text-gray-500">
              No repairs found for the current filters.
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-4">
            {/* Prev Button */}
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-2 py-1 rounded bg-gray-200 dark:bg-zinc-900 text-sm hover:bg-blue-200 disabled:opacity-50"
            >
              ←
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => {
                // Always show 1, 2 and last 2
                if (p === 1 || p === 2 || p === totalPages || p === totalPages - 1) return true;
                // Always show current page
                if (p === currentPage) return true;
                return false;
              })
              .map((p, i, arr) => {
                const prevPage = arr[i - 1];
                return (
                  <span key={p} className="flex items-center gap-2">
                    {/* Show dots for skipped pages */}
                    {prevPage && p - prevPage > 1 && <span className="px-2">...</span>}

                    <button
                      onClick={() => setCurrentPage(p)}
                      className={`px-3 py-1 rounded-full text-sm ${currentPage === p
                        ? "bg-blue-600 text-white font-semibold"
                        : "bg-gray-100 dark:bg-[var(--theme-color)] hover:bg-blue-100"
                        }`}
                    >
                      {p}
                    </button>
                  </span>
                );
              })}

            {/* Next Button */}
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-2 py-1 rounded bg-gray-200 dark:bg-zinc-900 text-sm hover:bg-blue-200 disabled:opacity-50"
            >
              →
            </button>
          </div>
        )}
      </div>

      {/* View Details Modal */}
      {showDetails && selectedRepair && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <div className="relative">
            <button
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-800 text-2xl font-bold z-10"
              onClick={() => setShowDetails(false)}
              aria-label="Close"
            >
              &times;
            </button>
            <RepairDetails
              repair={selectedRepair}
              onClose={() => setShowDetails(false)}
            />
          </div>
        </div>
      )}
      {showEdit && editRepair && (
        <EditRepairModal
          repair={editRepair}
          onClose={() => {
            setShowEdit(false);
            setEditRepair(null);
          }}
        />
      )}

    </div>
  );
};

export default DashboardRepairList;